import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatChartDate } from "@/lib/intelligenceFormat";
import type { DailyOperationsTrendPoint } from "@/api/intelligence";

type Props = {
	dailyTrend: DailyOperationsTrendPoint[];
};

export function OperationsTrendChart({ dailyTrend }: Props) {
	const chartData = useMemo(
		() => ({
			labels: dailyTrend.map((p) => formatChartDate(p.date)),
			datasets: [
				{
					label: "IPD Admissions",
					data: dailyTrend.map((p) => p.ipdAdmissions),
					borderColor: "rgb(79, 70, 229)",
					tension: 0.25,
					fill: false,
				},
				{
					label: "IPD Discharges",
					data: dailyTrend.map((p) => p.ipdDischarges),
					borderColor: "rgb(244, 63, 94)",
					tension: 0.25,
					fill: false,
				},
				{
					label: "OPD Visits",
					data: dailyTrend.map((p) => p.opdVisits),
					borderColor: "rgb(16, 185, 129)",
					tension: 0.25,
					fill: false,
				},
			],
		}),
		[dailyTrend]
	);

	const options = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: "index" as const, intersect: false },
			plugins: { legend: { position: "bottom" as const } },
		}),
		[]
	);

	if (dailyTrend.length === 0) {
		return (
			<Card>
				<CardContent className="py-8 text-center text-sm text-muted-foreground">
					No trend data
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-base font-medium">Daily operations trend</CardTitle>
				<p className="text-xs text-muted-foreground">IST calendar days in selected period</p>
			</CardHeader>
			<CardContent>
				<div className="h-[280px]">
					<Line data={chartData} options={options} />
				</div>
			</CardContent>
		</Card>
	);
}
